import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  Zap, Play, RefreshCw, Smartphone, Globe, Star, Search, MapPin, Mail,
  MessageSquare, Activity, Check, Heart, UserPlus, UserMinus,
  UserCheck, BookOpen, Send, Scan, MessageCircle, Settings
} from 'lucide-react';
import axios from 'axios';
import AutomationBanner from '../components/AutomationBanner';

/* ═══════════════════════════════════════════════
   ANIMATIONS
   ═══════════════════════════════════════════════ */
const slideIn = keyframes`from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}`;

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */
const Page = styled.div`width: 100%;`;

const ContentGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;
const LeftCol = styled.div`display: flex; flex-direction: column; gap: 14px;`;

const ScriptTypeBtn = styled.button`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid transparent; cursor: pointer; width: 100%;
  font-size: 12px; font-weight: 700; transition: all .15s;
  background: ${p => p.$selected ? `${p.$color}22` : `${p.$color}08`};
  color: ${p => p.$color || 'var(--text-muted)'};
  border-color: ${p => p.$selected ? `${p.$color}55` : 'transparent'};
  opacity: ${p => p.$selected ? 1 : .6};
  &:hover { opacity: 1; transform: translateY(-1px); }
`;
const ScriptIcon = styled.div`
  width: 36px; height: 36px; border-radius: 10px;
  background: ${p => `${p.$color}18`}; color: ${p => p.$color};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;

const ConfigPanel = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 20px; display: flex; flex-direction: column; gap: 14px;
`;
const ConfigTitle = styled.div`font-size: 16px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;
const ConfigDesc = styled.div`font-size: 12px; color: var(--text-muted); line-height: 1.6;`;

const ParamLabel = styled.label`
  font-size: 9px; font-weight: 600; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .3px; margin-bottom: 2px; display: block;
`;
const ParamInput = styled.input`
  width: 100%; padding: 7px 10px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px;
  &:focus { border-color: var(--primary); outline: none; }
`;
const ParamTextarea = styled.textarea`
  width: 100%; padding: 7px 10px; border-radius: 8px; min-height: 50px; resize: vertical;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px; font-family: inherit;
  &:focus { border-color: var(--primary); outline: none; }
`;

const LaunchBtn = styled.button`
  padding: 10px 20px; border-radius: 10px; border: none; font-weight: 700; font-size: 13px;
  background: ${p => p.$color || '#4285F4'}; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px; transition: all .15s;
  &:hover { opacity: .9; transform: translateY(-1px); }
  &:disabled { opacity: .4; cursor: not-allowed; transform: none; }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 40vh; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
`;

/* ── Right panel: Activity ── */
const ActivityPanel = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  display: flex; flex-direction: column; max-height: 75vh; overflow: hidden;
`;
const ActivityHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  font-size: 15px; font-weight: 700; color: var(--text-primary);
`;
const ActivityCount = styled.span`font-size: 12px; font-weight: 600; color: var(--text-muted);`;
const ActivityList = styled.div`
  flex: 1; overflow-y: auto; padding: 4px 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;
const DaySeparator = styled.div`
  padding: 4px 18px; font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .5px; color: var(--text-muted); background: var(--surface);
  border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
  position: sticky; top: 0; z-index: 1;
`;
const ActivityItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 18px; transition: background .1s;
  background: ${p => p.$alt ? 'rgba(66,133,244,.03)' : 'transparent'};
  ${p => p.$isNew ? css`animation: ${slideIn} .4s ease-out;` : ''}
  &:hover { background: var(--surface-hover); }
`;
const ActionIconWrap = styled.div`
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.$bg || 'rgba(66,133,244,.1)'};
  color: ${p => p.$color || '#4285F4'};
  position: relative;
  overflow: hidden;
  img {
    width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
    position: absolute; top: 0; left: 0;
  }
`;
const ActionText = styled.div`
  flex: 1; font-size: 13px; color: var(--text-secondary);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  strong { font-weight: 700; color: var(--text-primary); }
`;
const ActionTime = styled.div`
  font-size: 12px; color: var(--text-muted); font-family: 'JetBrains Mono', monospace;
  font-weight: 600; white-space: nowrap; flex-shrink: 0;
`;

/* ═══════════════════════════════════════════════
   SCRIPT DEFINITIONS
   ═══════════════════════════════════════════════ */
const GOOGLE_SCRIPTS = [
  {
    id: 'google-maps-review', name: 'Avis Google Maps', icon: Star, color: '#fbbc04',
    endpoint: '/api/google-scripts/maps-review',
    description: 'Automatiser le dépôt d\'avis sur Google Maps. Ouvre Maps, cherche le lieu, et laisse un avis avec une note.',
    params: [
      { key: 'place_name', label: 'Nom du lieu', default: '', type: 'text' },
      { key: 'rating', label: 'Note (1-5)', default: '5', type: 'number' },
      { key: 'review_text', label: 'Texte de l\'avis', default: '', type: 'textarea' },
    ]
  },
  {
    id: 'google-maps-profile', name: 'Profil Google Maps', icon: MapPin, color: '#34a853',
    endpoint: '/api/google-scripts/maps-profile',
    description: 'Analyser le profil Google connecté dans Maps : nom, email, contributions, avis déposés.',
    params: [
      { key: 'target_email', label: 'Email cible (optionnel)', default: '', type: 'text' },
    ]
  },
  {
    id: 'google-search-seo', name: 'Recherche Google (SEO)', icon: Search, color: '#4285f4',
    endpoint: '/api/google-scripts/search-seo',
    description: 'Effectuer des recherches Google depuis le device et cliquer sur un résultat cible pour améliorer le CTR SEO.',
    params: [
      { key: 'search_query', label: 'Requête de recherche', default: '', type: 'text' },
      { key: 'target_url', label: 'URL cible à cliquer', default: '', type: 'text' },
      { key: 'scroll_pages', label: 'Pages à scroller', default: '3', type: 'number' },
      { key: 'stay_duration', label: 'Temps sur site (sec)', default: '30-60', type: 'text' },
    ]
  },
  {
    id: 'google-search-batch', name: 'Recherches en lot', icon: Globe, color: '#4285f4',
    endpoint: '/api/google-scripts/search-batch',
    description: 'Effectuer plusieurs recherches Google depuis un fichier ou une liste pour simuler du trafic organique.',
    params: [
      { key: 'queries', label: 'Requêtes (1 par ligne)', default: '', type: 'textarea' },
      { key: 'delay_between', label: 'Délai entre recherches (sec)', default: '10-30', type: 'text' },
    ]
  },
  {
    id: 'google-youtube-watch', name: 'YouTube Watch', icon: Play, color: '#ff0000',
    endpoint: '/api/google-scripts/youtube-watch',
    description: 'Regarder des vidéos YouTube pour augmenter les vues. Ouvre l\'app YouTube et regarde pour une durée configurable.',
    params: [
      { key: 'video_url', label: 'URL vidéo ou recherche', default: '', type: 'text' },
      { key: 'watch_duration', label: 'Durée de visionnage (sec)', default: '60-180', type: 'text' },
      { key: 'like_video', label: 'Liker la vidéo (oui/non)', default: 'non', type: 'text' },
    ]
  },
  {
    id: 'google-gmail-detect', name: 'Détecter Gmail', icon: MessageSquare, color: '#ea4335',
    endpoint: '/api/gmail-scripts/detect-gmail',
    description: 'Détecter le compte Gmail principal connecté sur le device via les paramètres Android.',
    params: []
  },
];

/* ── Action map for Google logs ── */
const ACTION_MAP = {
  'script_start':  { label: 'Script démarré', icon: Play, color: '#4285f4', bg: 'rgba(66,133,244,.12)' },
  'script_end':    { label: 'Script terminé', icon: Check, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'review_posted': { label: 'Avis déposé', icon: Star, color: '#fbbc04', bg: 'rgba(251,188,4,.12)' },
  'search':        { label: 'Recherche Google', icon: Search, color: '#4285f4', bg: 'rgba(66,133,244,.12)' },
  'click':         { label: 'Clic sur résultat', icon: Globe, color: '#34a853', bg: 'rgba(52,168,83,.12)' },
  'youtube_watch': { label: 'YouTube regardé', icon: Play, color: '#ff0000', bg: 'rgba(255,0,0,.12)' },
  'google_action': { label: 'Action Google', icon: Globe, color: '#4285f4', bg: 'rgba(66,133,244,.12)' },
  // Instagram action types (for unified panel)
  'LikeAction':        { label: 'Liked post from', icon: Heart, color: '#ef4444', bg: 'rgba(239,68,68,.12)', showProfilePic: true },
  'FollowAction':      { label: 'Followed', icon: UserPlus, color: '#22c55e', bg: 'rgba(34,197,94,.12)', showProfilePic: true },
  'GetProfileAction':  { label: 'Viewed profile', icon: UserCheck, color: '#a591ef', bg: 'rgba(165,145,239,.12)', showProfilePic: true },
  'StoryWatchAction':  { label: 'Viewed story from', icon: BookOpen, color: '#f59e0b', bg: 'rgba(245,158,11,.12)', showProfilePic: true },
  'StoryLikeAction':   { label: 'Liked story from', icon: Star, color: '#ec4899', bg: 'rgba(236,72,153,.12)', showProfilePic: true },
  'UnfollowAction':    { label: 'Unfollowed', icon: UserMinus, color: '#f97316', bg: 'rgba(249,115,22,.12)', showProfilePic: true },
  'CommentAction':     { label: 'Commented on', icon: MessageCircle, color: '#3b82f6', bg: 'rgba(59,130,246,.12)', showProfilePic: true },
  'DirectMessageAction': { label: 'DM to', icon: Send, color: '#8b5cf6', bg: 'rgba(139,92,246,.12)', showProfilePic: true },
  'ScrapeAction':      { label: 'Scraped', icon: Scan, color: '#06b6d4', bg: 'rgba(6,182,212,.12)' },
  // Gmail action types
  'input_firstname':   { label: 'Pr\u00e9nom saisi', icon: UserCheck, color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
  'input_lastname':    { label: 'Nom saisi', icon: UserCheck, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'input_email':       { label: 'Email saisi', icon: Mail, color: '#0ea5e9', bg: 'rgba(14,165,233,.12)' },
  'input_password':    { label: 'Mot de passe', icon: Settings, color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  'gmail_action':      { label: 'Action Gmail', icon: Mail, color: '#EA4335', bg: 'rgba(234,67,53,.12)' },
};

/* ── Helpers ── */
const fmtTime = (ts) => { if (!ts) return ''; try { return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const fmtAgo = (ts) => { if (!ts) return ''; const d = (Date.now() - new Date(ts).getTime()) / 1000; if (d < 60) return 'à l\'instant'; if (d < 3600) return `il y a ${Math.floor(d / 60)}m`; if (d < 86400) return `il y a ${Math.floor(d / 3600)}h`; return `il y a ${Math.floor(d / 86400)}j`; };
const fmtDateLabel = (ts) => { if (!ts) return ''; const d = new Date(ts); const now = new Date(); if (d.toDateString() === now.toDateString()) return "Aujourd'hui"; const y = new Date(now); y.setDate(y.getDate() - 1); if (d.toDateString() === y.toDateString()) return 'Hier'; return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); };

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */
const GoogleAutomation = ({ selectedDevice, onNavigate, activeTab: currentTab }) => {
  const deviceId = selectedDevice?.device_id;
  const [selectedScript, setSelectedScript] = useState(GOOGLE_SCRIPTS[0].id);
  const [scriptParams, setScriptParams] = useState({});
  const [launching, setLaunching] = useState({});
  const [, setStatuses] = useState({});
  const [actions, setActions] = useState([]);
  const pollRef = useRef(null);

  useEffect(() => {
    const defaults = {};
    GOOGLE_SCRIPTS.forEach(s => { defaults[s.id] = {}; s.params.forEach(p => { defaults[s.id][p.key] = p.default; }); });
    setScriptParams(defaults);
  }, []);

  const updateParam = (scriptId, key, value) => {
    setScriptParams(prev => ({ ...prev, [scriptId]: { ...prev[scriptId], [key]: value } }));
  };

  // Fetch Google logs
  const fetchGoogleActions = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await fetch(`/api/google-scripts/logs/${deviceId}`);
      const d = await r.json();
      setActions((d.actions || []).map(a => ({
        id: `google-${a.id || a.timestamp}`, type: a.action_type || 'google_action',
        timestamp: a.timestamp, target_user: a.data?.value || a.message || '—',
        _service: 'google', _status: a.status, _message: a.message,
      })));
    } catch { /* silent */ }
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) return;
    fetchGoogleActions();
    pollRef.current = setInterval(() => { fetchGoogleActions(); }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchGoogleActions, deviceId]);

  const launchScript = async (script) => {
    if (!deviceId) return alert('Sélectionnez un device');
    setLaunching(prev => ({ ...prev, [script.id]: true }));
    try {
      const r = await axios.post(script.endpoint, { device_id: deviceId, ...scriptParams[script.id] });
      if (r.data.success) setStatuses(prev => ({ ...prev, [script.id]: 'running' }));
    } catch (e) { alert('Erreur: ' + (e.response?.data?.error || e.message)); }
    setLaunching(prev => ({ ...prev, [script.id]: false }));
  };

  if (!selectedDevice) {
    return (<Page><EmptyState><Smartphone size={48} /><p style={{ fontSize: 14 }}>Sélectionnez un appareil pour l'automatisation Google</p></EmptyState></Page>);
  }

  const activeScript = GOOGLE_SCRIPTS.find(s => s.id === selectedScript) || GOOGLE_SCRIPTS[0];
  const ActiveIcon = activeScript.icon;

  // Google actions only
  const filtered = [...actions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <Page>
      <AutomationBanner
        platform="google"
        deviceName={selectedDevice.device_name || deviceId}
        isConnected={selectedDevice?.isConnected || selectedDevice?.is_online}
        onNavigate={onNavigate}
      />

      <ContentGrid>
        <LeftCol>
          {GOOGLE_SCRIPTS.map(s => {
            const SIcon = s.icon;
            return (
              <ScriptTypeBtn key={s.id} $color={s.color} $selected={selectedScript === s.id} onClick={() => setSelectedScript(s.id)}>
                <ScriptIcon $color={s.color}><SIcon size={18} /></ScriptIcon>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 700 }}>{s.name}</div>
                  <div style={{ fontSize: 10, opacity: .7, fontWeight: 500 }}>{s.description.substring(0, 60)}...</div>
                </div>
              </ScriptTypeBtn>
            );
          })}

          <ConfigPanel>
            <ConfigTitle>
              <ScriptIcon $color={activeScript.color}><ActiveIcon size={18} /></ScriptIcon>
              {activeScript.name}
            </ConfigTitle>
            <ConfigDesc>{activeScript.description}</ConfigDesc>

            {activeScript.params.map(param => (
              <div key={param.key}>
                <ParamLabel>{param.label}</ParamLabel>
                {param.type === 'textarea' ? (
                  <ParamTextarea
                    value={scriptParams[activeScript.id]?.[param.key] || ''}
                    onChange={e => updateParam(activeScript.id, param.key, e.target.value)}
                    placeholder={param.default || param.label}
                  />
                ) : (
                  <ParamInput
                    type={param.type === 'number' ? 'number' : 'text'}
                    value={scriptParams[activeScript.id]?.[param.key] || ''}
                    onChange={e => updateParam(activeScript.id, param.key, e.target.value)}
                    placeholder={param.default || param.label}
                  />
                )}
              </div>
            ))}

            <LaunchBtn $color={activeScript.color} onClick={() => launchScript(activeScript)} disabled={launching[activeScript.id]}>
              {launching[activeScript.id] ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Lancement...</>
                : <><Play size={13} /> Lancer {activeScript.name}</>}
            </LaunchBtn>
          </ConfigPanel>
        </LeftCol>

        {/* ── Right: Unified Actions Panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ActivityPanel>
            <ActivityHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Zap size={16} style={{ color: '#4285F4' }} />
                Dernières actions
                {filtered[0] && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>— {fmtAgo(filtered[0].timestamp)}</span>}
              </div>
              <ActivityCount>{actions.length} événements</ActivityCount>
            </ActivityHeader>

            <ActivityList>
              {filtered.length === 0 ? (
                <EmptyState style={{ padding: 30 }}>
                  <Activity size={32} />
                  <p>Aucune activité récente</p>
                </EmptyState>
              ) : (() => {
                let lastDay = null; let dayIdx = 0;
                return filtered.map(a => {
                  const meta = ACTION_MAP[a.type] || { label: a._message || a.type || 'Action', icon: Activity, color: 'var(--text-muted)', bg: 'var(--surface)' };
                  const Icon = meta.icon;
                  const actionDate = a.timestamp ? new Date(a.timestamp) : null;
                  const dayKey = actionDate ? `${actionDate.getFullYear()}-${actionDate.getMonth()}-${actionDate.getDate()}` : null;
                  let showSep = false;
                  if (dayKey && dayKey !== lastDay) { showSep = true; if (lastDay !== null) dayIdx++; lastDay = dayKey; }
                  const alt = dayIdx % 2 === 1;
                  return (
                    <React.Fragment key={a.id}>
                      {showSep && <DaySeparator>{fmtDateLabel(a.timestamp)}</DaySeparator>}
                      <ActivityItem $alt={alt}>
                        <ActionIconWrap $bg={meta.bg} $color={meta.color}>
                          <Icon size={14} />
                        </ActionIconWrap>
                        <ActionText>
                          {a._message || meta.label} {a.target_user && a.target_user !== '—' && <strong>{a.target_user}</strong>}
                        </ActionText>
                        <ActionTime>
                          {fmtTime(a.timestamp)}
                        </ActionTime>
                      </ActivityItem>
                    </React.Fragment>
                  );
                });
              })()}
            </ActivityList>
          </ActivityPanel>
        </div>
      </ContentGrid>
    </Page>
  );
};

export default GoogleAutomation;
